import React, { useRef, useEffect } from 'react';

const GPUCountSelector = ({ counts, selectedCount, onSelectCount, disabled, isLoading }) => {
  // Find the index of the selected count in the counts array
  const selectedIndex = counts.findIndex(c => c.value === selectedCount);
  const sliderRef = useRef(null);
  
  // Handle slider change with smoother transitions
  const handleSliderChange = (e) => {
    if (!disabled) {
      const val = parseFloat(e.target.value);
      // Find the closest valid index based on the slider value
      const closestIndex = Math.round(val);
      onSelectCount(counts[closestIndex].value);
    }
  };

  // Add smooth slider behavior with CSS variables
  useEffect(() => {
    if (sliderRef.current) {
      // Update custom slider properties for smoother interaction
      sliderRef.current.style.setProperty('--slider-index', selectedIndex);
    }
  }, [selectedIndex]);

  // Calculate the exact percentage for the performance bar
  const getPerformanceWidth = () => {
    if (selectedCount === 1) return "1%";      // Minimal visual indicator for 1×
    if (selectedCount === 2) return "33.3%";   // 2× at 1/3 of the way
    if (selectedCount === 4) return "66.6%";   // 4× at 2/3 of the way
    if (selectedCount === 8) return "100%";    // 8× at full width
    return "0%";
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="bg-[#121212] p-4 sm:p-3 md:p-1 rounded-md mb-4 md:mb-1 w-full  min-h-[180px] sm:min-h-[170px] md:min-h-[160px] animate-pulse">
        <h3 className="font-semibold text-white text-base sm:text-sm mb-4 sm:mb-3 md:mb-1">GPU Count</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-2 md:gap-1">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="relative p-4 sm:p-3 md:p-2 rounded-md bg-[#1d1d1d] h-24 sm:h-20 md:h-16"></div>
          ))}
        </div>
        <div className="mt-4 md:mt-2 relative">
          <p className="text-white text-sm sm:text-xs mb-2 md:mb-1">Performance Scale</p>
          <div className="w-full h-4 sm:h-3 md:h-2 bg-[#1d1d1d] rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#121212] p-4 sm:p-3 md:p-1 rounded-md mb-4 md:mb-1 w-full md:w-[90%] md:mx-auto min-h-[180px] sm:min-h-[170px] md:min-h-[160px]">
      <h3 className="font-semibold text-white text-base sm:text-sm mb-4 sm:mb-3 md:mb-1">GPU Count</h3>
      
      {/* Card grid - responsive: 2 cols on mobile, 4 cols on larger screens */}
      {counts.length === 0 ? (
        <div className="text-center text-gray-300 text-sm py-8 md:py-6 min-h-[120px] md:min-h-[100px] flex items-center justify-center">
          No GPU counts available
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-2 md:gap-1">
            {counts.map((count) => (
              <div 
                key={count.value}
                className={`relative p-4 sm:p-3 md:p-2 flex flex-col items-center rounded-md transition-all cursor-pointer min-h-[80px] sm:min-h-[72px] md:min-h-[64px] ${
                  disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : selectedCount === count.value
                      ? 'bg-[#1a2a1a] border border-[#e0eacc]' 
                      : 'bg-[#1d1d1d] border border-[#333333] hover:border-gray-500'
                }`}
                onClick={() => !disabled && onSelectCount(count.value)}
              >
                {/* GPU icon representation - improved mobile sizes */}
                <div className="h-12 sm:h-10 md:h-8 flex items-center justify-center mb-2">
                  {count.value === 1 && (
                    <div className={`w-16 sm:w-14 md:w-12 h-8 sm:h-7 md:h-6 ${selectedCount === count.value ? 'bg-[#e0eacc]' : 'bg-[#333333]'} rounded-sm`}></div>
                  )}
                  {count.value === 2 && (
                    <div className="flex gap-1">
                      <div className={`w-7 sm:w-6 md:w-5 h-8 sm:h-7 md:h-6 ${selectedCount === count.value ? 'bg-[#e0eacc]' : 'bg-[#333333]'} rounded-sm`}></div>
                      <div className={`w-7 sm:w-6 md:w-5 h-8 sm:h-7 md:h-6 ${selectedCount === count.value ? 'bg-[#e0eacc]' : 'bg-[#333333]'} rounded-sm`}></div>
                    </div>
                  )}
                  {count.value === 4 && (
                    <div className="grid grid-cols-2 gap-1">
                      <div className={`w-7 sm:w-6 md:w-5 h-5 sm:h-4 md:h-3 ${selectedCount === count.value ? 'bg-[#e0eacc]' : 'bg-[#333333]'} rounded-sm`}></div>
                      <div className={`w-7 sm:w-6 md:w-5 h-5 sm:h-4 md:h-3 ${selectedCount === count.value ? 'bg-[#e0eacc]' : 'bg-[#333333]'} rounded-sm`}></div>
                      <div className={`w-7 sm:w-6 md:w-5 h-5 sm:h-4 md:h-3 ${selectedCount === count.value ? 'bg-[#e0eacc]' : 'bg-[#333333]'} rounded-sm`}></div>
                      <div className={`w-7 sm:w-6 md:w-5 h-5 sm:h-4 md:h-3 ${selectedCount === count.value ? 'bg-[#e0eacc]' : 'bg-[#333333]'} rounded-sm`}></div>
                    </div>
                  )}
                  {count.value === 8 && (
                    <div className="grid grid-cols-4 gap-0.5">
                      <div className={`w-4 sm:w-3 md:w-2.5 h-4 sm:h-3 md:h-2.5 ${selectedCount === count.value ? 'bg-[#e0eacc]' : 'bg-[#333333]'} rounded-sm`}></div>
                      <div className={`w-4 sm:w-3 md:w-2.5 h-4 sm:h-3 md:h-2.5 ${selectedCount === count.value ? 'bg-[#e0eacc]' : 'bg-[#333333]'} rounded-sm`}></div>
                      <div className={`w-4 sm:w-3 md:w-2.5 h-4 sm:h-3 md:h-2.5 ${selectedCount === count.value ? 'bg-[#e0eacc]' : 'bg-[#333333]'} rounded-sm`}></div>
                      <div className={`w-4 sm:w-3 md:w-2.5 h-4 sm:h-3 md:h-2.5 ${selectedCount === count.value ? 'bg-[#e0eacc]' : 'bg-[#333333]'} rounded-sm`}></div>
                      <div className={`w-4 sm:w-3 md:w-2.5 h-4 sm:h-3 md:h-2.5 ${selectedCount === count.value ? 'bg-[#e0eacc]' : 'bg-[#333333]'} rounded-sm`}></div>
                      <div className={`w-4 sm:w-3 md:w-2.5 h-4 sm:h-3 md:h-2.5 ${selectedCount === count.value ? 'bg-[#e0eacc]' : 'bg-[#333333]'} rounded-sm`}></div>
                      <div className={`w-4 sm:w-3 md:w-2.5 h-4 sm:h-3 md:h-2.5 ${selectedCount === count.value ? 'bg-[#e0eacc]' : 'bg-[#333333]'} rounded-sm`}></div>
                      <div className={`w-4 sm:w-3 md:w-2.5 h-4 sm:h-3 md:h-2.5 ${selectedCount === count.value ? 'bg-[#e0eacc]' : 'bg-[#333333]'} rounded-sm`}></div>
                    </div>
                  )}
                </div>
                
                {/* Count label - improved mobile text size */}
                <span className={`text-base sm:text-sm font-bold ${selectedCount === count.value ? 'text-[#e0eacc]' : 'text-white'}`}>
                  {count.value}×
                </span>
              </div>
            ))}
          </div>
          
          {/* Performance indicator with slider functionality */}
          {!disabled && (
            <div className="mt-4 md:mt-2 relative">
              <p className="text-white text-sm sm:text-xs mb-3 sm:mb-2 md:mb-1">Performance Scale</p>
              <div className="w-full h-4 sm:h-3 md:h-2 bg-[#1d1d1d] rounded-full overflow-hidden">
                <div 
                  className="h-4 sm:h-3 md:h-2 bg-[#e0eacc] rounded-full transition-width duration-150 ease-out min-w-[1px]" 
                  style={{ width: getPerformanceWidth() }}
                ></div>
              </div>
              <div className="relative">
                <input
                  ref={sliderRef}
                  type="range"
                  min="0"
                  max={counts.length - 1}
                  step="0.01"
                  value={selectedIndex}
                  onChange={handleSliderChange}
                  disabled={disabled}
                  className="slider-input absolute w-full h-12 sm:h-10 md:h-8 top-[-40px] sm:top-[-32px] md:top-[-27px] opacity-0 cursor-pointer"
                />
              </div>
              <div className="flex justify-between text-sm sm:text-xs text-gray-400 mt-3 sm:mt-2 md:mt-1">
                <span className={selectedCount === 1 ? 'text-[#e0eacc]' : ''}>1×</span>
                <span className={selectedCount === 2 ? 'text-[#e0eacc]' : ''}>2×</span>
                <span className={selectedCount === 4 ? 'text-[#e0eacc]' : ''}>4×</span>
                <span className={selectedCount === 8 ? 'text-[#e0eacc]' : ''}>8×</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default React.memo(GPUCountSelector);